/**
 * ============================================================================
 * RAISE ISSUE COMPONENT
 * ============================================================================
 * 
 * Screen 1: Raise Issue
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispute } from '../../hooks/useDispute';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Input from '../common/Input';
import Textarea from '../common/Textarea';
import Card from '../common/Card';
import ProgressBar from '../common/ProgressBar';
import EvidenceUpload from './EvidenceUpload';

/**
 * Raise Issue Component
 * @param {Object} props
 * @param {string} props.transactionId - Transaction ID
 * @param {Object} props.transaction - Transaction object
 * @param {Function} props.onDisputeCreated - Callback when dispute is created
 * @returns {JSX.Element}
 */
const RaiseIssue = ({ transactionId: propTransactionId, transaction, onDisputeCreated }) => {
  const navigate = useNavigate();
  const { id: paramTransactionId } = useParams();
  const transactionId = propTransactionId || paramTransactionId;
  const { createDispute, loading } = useDispute();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    issueType: '',
    description: '',
    resolutionType: 'full_refund',
    refundPercentage: 100,
    evidence: [],
  });

  const [errors, setErrors] = useState({});

  const issueTypes = [
    {
      id: 'non_delivery',
      label: language === 'fr' ? 'Non-livraison' : 'Non-Delivery',
      icon: '📦',
    },
    {
      id: 'damaged',
      label: language === 'fr' ? 'Article endommagé' : 'Damaged Item',
      icon: '💔',
    },
    {
      id: 'wrong_item',
      label: language === 'fr' ? 'Mauvais article' : 'Wrong Item',
      icon: '🔄',
    },
    {
      id: 'quality',
      label: language === 'fr' ? 'Problème de qualité' : 'Quality Issue',
      icon: '⭐',
    },
    {
      id: 'other',
      label: language === 'fr' ? 'Autre' : 'Other',
      icon: '❓',
    },
  ];

  const resolutionTypes = [
    {
      id: 'full_refund',
      label: language === 'fr' ? 'Remboursement complet' : 'Full refund',
      amount: '100%',
    },
    {
      id: 'partial_refund',
      label: language === 'fr' ? 'Remboursement partiel' : 'Partial refund',
      amount: language === 'fr' ? 'Pourcentage personnalisé' : 'Custom %',
    },
    {
      id: 'revision',
      label: language === 'fr' ? 'Demander une révision' : 'Request revision',
      amount: language === 'fr' ? 'Donner au vendeur une chance de corriger' : 'Give seller chance to fix',
    },
    {
      id: 'extend_deadline',
      label: language === 'fr' ? 'Prolonger le délai' : 'Extend deadline',
      amount: language === 'fr' ? 'Ajouter plus de temps' : 'Add more time',
    },
  ];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.issueType) {
      newErrors.issueType = 'Please select an issue type';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Please describe the issue';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    try {
      const disputeData = {
        transactionId: transactionId || transaction?.id,
        issueType: formData.issueType,
        description: formData.description,
        resolutionType: formData.resolutionType,
        refundPercentage: formData.resolutionType === 'partial_refund' ? formData.refundPercentage : null,
        evidence: formData.evidence,
      };

      const dispute = await createDispute(disputeData);
      showSuccess(
        language === 'fr'
          ? 'Litige créé avec succès!'
          : 'Issue raised successfully!'
      );
      
      if (onDisputeCreated) {
        onDisputeCreated(dispute);
      } else {
        navigate(`/disputes/${dispute.id || dispute.disputeId}`);
      }
    } catch (error) {
      showError(
        error.message ||
          (language === 'fr' ? 'Échec de la création du litige' : 'Failed to raise issue')
      );
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-6 animate-fadeInUp">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {language === 'fr' ? 'Étape' : 'Step'} {step} {language === 'fr' ? 'sur' : 'of'} 2
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {Math.round((step / 2) * 100)}%
          </span>
        </div>
        <ProgressBar value={(step / 2) * 100} variant="info" animated />
      </div>

      {/* Step 1: Issue Details */}
        {step === 1 && (
          <Card variant="glass" hover className="animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            <div className="space-y-6">
              {/* Issue Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  What's the problem? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {issueTypes.map((type, index) => (
                    <label
                      key={type.id}
                      className={`
                        flex items-center p-4 border-2 rounded-xl cursor-pointer
                        transition-all duration-200 transform hover:scale-105
                        ${
                          formData.issueType === type.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }
                        animate-fadeInUp
                      `}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <input
                        type="radio"
                        name="issueType"
                        value={type.id}
                        checked={formData.issueType === type.id}
                        onChange={(e) => handleChange('issueType', e.target.value)}
                        className="mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-2xl mr-2">{type.icon}</span>
                      <span className="text-gray-900 dark:text-white font-medium">
                        {type.label}
                      </span>
                    </label>
                  ))}
                </div>
                {errors.issueType && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400 animate-fadeIn">
                    {errors.issueType}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <Textarea
                  label={language === 'fr' ? 'Décrivez le problème' : 'Describe the issue'}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={
                    language === 'fr'
                      ? 'Le site web n\'a que 3 pages au lieu de 5 comme convenu...'
                      : 'The website has only 3 pages instead of 5 as agreed...'
                  }
                  rows={6}
                  error={errors.description}
                  required
                  maxLength={2000}
                />
              </div>

              {/* Evidence Upload */}
              <div>
                <EvidenceUpload
                  files={formData.evidence}
                  onChange={(files) => handleChange('evidence', files)}
                  maxFiles={10}
                />
              </div>

              {/* Next Button */}
              <Button
                variant="gradient"
                size="xl"
                fullWidth
                onClick={handleNext}
                className="text-lg py-6 shadow-xl hover:shadow-2xl transition-all"
              >
                {language === 'fr' ? 'Continuer vers la résolution' : 'Continue to Resolution'} →
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Resolution Selection */}
        {step === 2 && (
          <Card variant="glass" hover className="animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {language === 'fr' ? 'Sélectionnez votre résolution préférée:' : 'Select your preferred resolution:'}
              </label>
              <div className="space-y-3">
                {resolutionTypes.map((type) => (
                  <label
                    key={type.id}
                    className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.resolutionType === type.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="resolutionType"
                      value={type.id}
                      checked={formData.resolutionType === type.id}
                      onChange={(e) => handleChange('resolutionType', e.target.value)}
                      className="mt-1 mr-3 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {type.label}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {type.amount}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Partial Refund Slider */}
            {formData.resolutionType === 'partial_refund' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'fr' ? 'Pourcentage de remboursement:' : 'Refund Percentage:'} {formData.refundPercentage}%
                </label>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={formData.refundPercentage}
                  onChange={(e) => handleChange('refundPercentage', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>1%</span>
                  <span>100%</span>
                </div>
              </div>
            )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  onClick={() => setStep(1)}
                  className="font-semibold"
                >
                  ← Back
                </Button>
                <Button
                  variant="gradient"
                  size="xl"
                  fullWidth
                  onClick={handleSubmit}
                  loading={loading}
                  className="text-lg py-6 shadow-xl hover:shadow-2xl transition-all"
                >
                  Submit Issue →
                </Button>
              </div>
            </div>
          </Card>
        )}
    </div>
  );
};

export default RaiseIssue;
